import * as React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/**
 * Reusable form input component with label, validation, and error display
 * Uses shadcn/ui components for modern design
 * @param {string} label - Label text for the input
 * @param {string} name - Field name (for focus/blur handlers)
 * @param {string} value - Input value
 * @param {Function} onChange - Change handler: (value) => void
 * @param {Function} onFocus - Optional focus handler
 * @param {Function} onBlur - Optional blur handler
 * @param {string} type - Input type (default: "text")
 * @param {string} placeholder - Placeholder text
 * @param {number} maxLength - Maximum length
 * @param {string} error - Validation error message
 * @param {string} className - Additional CSS classes
 * @param {object} ...props - Other input props
 */
const FormInput = React.forwardRef(({
  label,
  name,
  value,
  onChange,
  onFocus,
  onBlur,
  type = "text",
  placeholder = "",
  maxLength,
  error,
  className = "",
  ...props
}, ref) => {
  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const handleFocus = () => {
    if (onFocus && name) {
      onFocus(name);
    }
  };

  const handleBlur = () => {
    if (onBlur && name) {
      onBlur(name);
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <Label htmlFor={name} className={cn(error && "text-destructive")}>
          {label}
        </Label>
      )}
      <Input
        ref={ref}
        id={name}
        type={type}
        value={value || ""}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        className={cn(
          "placeholder:text-muted-foreground/60 placeholder:italic",
          error && "border-destructive focus-visible:ring-destructive"
        )}
        {...props}
      />
      {error && (
        <p className="text-sm font-medium text-destructive">{error}</p>
      )}
    </div>
  );
});

FormInput.displayName = "FormInput";

export default FormInput;
